// ---------------------------------------------------------------------------
// Activity export.
//
// Reported defect: `orders-page.tsx`'s "Export" button rendered with no
// `onClick` at all — it looked pressable, and pressing it did nothing, said
// nothing, and downloaded nothing. This module is the action behind it.
//
// It exports exactly the rows the screen is showing (the active status tab's
// filter, every page of it — not just the visible page), and nothing that was
// not read from the server. When there is nothing to export the page renders
// the button `disabled` with a visible reason instead of calling this.
// ---------------------------------------------------------------------------

import type { OrderRecord } from '@/types/dashboard'

/** Header row — the same columns, in the same order, as the on-screen table. */
const COLUMNS: Array<{ header: string; read: (row: OrderRecord) => string }> = [
  { header: '#', read: (row) => row.id },
  { header: 'Actor', read: (row) => row.customer_name },
  { header: 'Actor email', read: (row) => row.customer_email },
  { header: 'Artifact', read: (row) => row.app_name },
  { header: 'Changes', read: (row) => String(row.amount) },
  { header: 'State', read: (row) => row.status },
  { header: 'Updated', read: (row) => row.created_at },
]

/**
 * RFC 4180 field escaping. A field is quoted when it contains a comma, a
 * quote, or a line break; embedded quotes are doubled.
 */
export function escapeCsvField(value: string): string {
  if (!/[",\r\n]/.test(value)) return value
  return `"${value.replace(/"/g, '""')}"`
}

/** The rows as CSV text, header row first. */
export function activityRowsToCsv(rows: OrderRecord[]): string {
  const lines = [COLUMNS.map((column) => escapeCsvField(column.header)).join(',')]
  for (const row of rows) {
    lines.push(
      COLUMNS.map((column) => escapeCsvField(column.read(row) ?? '')).join(','),
    )
  }
  // Trailing newline so the last record is terminated, per RFC 4180.
  return `${lines.join('\r\n')}\r\n`
}

/** `cogo-activity-2026-07-31.csv` — date only, no clock, no invented id. */
export function activityExportFilename(now: Date = new Date()): string {
  const yyyy = now.getFullYear()
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const dd = String(now.getDate()).padStart(2, '0')
  return `cogo-activity-${yyyy}-${mm}-${dd}.csv`
}

/**
 * Writes the CSV to the user's download location.
 *
 * The BOM is deliberate: without it Excel reads a UTF-8 CSV as the local
 * codepage and mangles every non-ASCII actor or artifact name. This matches
 * `action-flow-engine.ts`'s CSV download in `@cogo/runtime-v2`.
 */
export function downloadActivityCsv(
  rows: OrderRecord[],
  now: Date = new Date(),
): string {
  const filename = activityExportFilename(now)
  const blob = new Blob([`\uFEFF${activityRowsToCsv(rows)}`], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
  return filename
}

/**
 * Why the Export control cannot run, in the words the screen uses — or `null`
 * when it can. Never a dead click: the page renders this string next to a
 * `disabled` button.
 *
 * `copy` follows the `planHeadline` precedent in `studio-billing-source.ts`:
 * the SCREEN passes the active locale's words, and the parameter defaults to
 * the English constants so a caller that does not (and this module's own unit
 * tests) reads exactly as before.
 */
export const EXPORT_BLOCKED_NOT_LOADED = 'Activity could not be loaded'
export const EXPORT_BLOCKED_NONE = 'No activity to export yet'

export function activityExportBlockedReason(
  status: 'loading' | 'ready' | 'error' | string,
  rowCount: number,
  copy?: { notLoaded: string; none: string },
): string | null {
  if (status === 'error') return copy?.notLoaded ?? EXPORT_BLOCKED_NOT_LOADED
  if (rowCount === 0) return copy?.none ?? EXPORT_BLOCKED_NONE
  return null
}
